/**
* @render react
* @name ListLayout
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { EnhancedListItem } from 'Components/molecules/EnhancedListItem';
import { Link } from 'Components/molecules/Link';

/**
 * ListLayout component
 *
 * @param {string} title - Title to display.
 * @param {string} subtitle - Subtitle to display.
 * @param {string} excerpt - Excerpt to display.
 * @param {string} url - URL for the whole list item
 * @param {string} image - Object containing image src and alt
 * @param {string} component - Component to be displayed instead of regular label
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 * @todo Use Heading component once it's ready.
 */
const ListLayout = (
  {
    title,
    subtitle,
    excerpt,
    url,
    image,
    component,
    loadStyles,
    showIndex,
    index,
    extraClassNames,
    context: { cssPrefix },
  },
) => {
  const componentsMap = {
    EnhancedListItem,
  };

  const TagName = componentsMap[component || ''];

  if (component) {
    return (
      <li
        className={classNames({
          [`${cssPrefix}__list-item`]: loadStyles,
          ...extraClassNames,
        })}
      >
        {showIndex && <span className={`${cssPrefix}__list-item--index`}>{`${index}`}</span>}
        <TagName title={title} image={image} subtitle={subtitle} excerpt={excerpt} />
      </li>
    );
  }

  return (
    <li
      className={classNames({
        [`${cssPrefix}__list-item`]: loadStyles,
        ...extraClassNames,
      })}
    >
      {url ? (
        <Link href={url}>
          {showIndex && <span className={`${cssPrefix}__list-item--index`}>{`${index}`}</span>}
          <span className={`${cssPrefix}__list-item--title`}>{title}</span>
          {subtitle && <span className={`${cssPrefix}__list-item--subtitle`}>{subtitle}</span>}
        </Link>
      ) : (
        <>
          {showIndex && <span className={`${cssPrefix}__list-item--index`}>{`${index}`}</span>}
          <span className={`${cssPrefix}__list-item--title`}>{title}</span>
          {subtitle && <span className={`${cssPrefix}__list-item--subtitle`}>{subtitle}</span>}
        </>
      )}
    </li>
  );
};

/**
 * Valid props
 */
ListLayout.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  url: PropTypes.string,
  subtitle: PropTypes.string,
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  excerpt: PropTypes.string,
  component: PropTypes.string,
  loadStyles: PropTypes.bool,
  showIndex: PropTypes.bool,
  index: PropTypes.number,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
};

/**
 * Default props
 */
ListLayout.defaultProps = {
  title: '',
  url: '',
  component: '',
  subtitle: '',
  excerpt: '',
  image: {
    src: '',
    alt: '',
  },
  loadStyles: true,
  showIndex: false,
  index: null,
  extraClassNames: {
  },
  context: {},
};

export default withPolaris(ListLayout);
